import type { PropsWithChildren, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Button, toastManager } from '@systemeio/components'
import { Trans, useTranslation } from 'next-i18next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { AccessDenied } from 'shared/errors/access-denied'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import type { DomainAssetsInterface } from 'shared/types/domain'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import JoinForm from 'modules/community/components/join-form'
import LoginForm from 'modules/community/components/login-form'
import RegisterForm from 'modules/community/components/register-form'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import Header from 'modules/header'
import { useBrand } from 'modules/white-label/contexts/brand-context'

type ModeType = 'join' | 'login' | 'register' | 'thank-you'

function Join(): ReactNode {
  const router = useRouter()
  const { t } = useTranslation()
  const { user, loading } = useUser()
  const { communityPath } = router.query
  const { data: community, isValidating: isCommunityLoading, error } = useCommunityWithTopics()
  const [mode, setMode] = useState<ModeType>('join')

  const restrictedCommunity = community?.ownerRestricted ?? false

  useEffect(() => {
    if (!loading && !isAuthorizedUser(user)) {
      setMode('register')
    }
  }, [user])

  useEffect(() => {
    if (error instanceof AccessDenied) {
      router.replace(`/community/403`)
    }
  }, [error])

  useEffect(() => {
    if (!restrictedCommunity || mode === 'thank-you') return

    const toastId = 'community-restricted'

    toastManager.add({
      id: toastId,
      type: 'error',
      timeout: 0,
      title: t('community.restricted.notification'),
    })

    return () => toastManager.close(toastId)
  }, [restrictedCommunity, t])

  if (!community || isCommunityLoading) {
    return <div />
  }

  return (
    <div
      className="mx-auto max-w-lg rounded-lg bg-bg-primary px-5 pt-8 pb-10 text-center shadow-lg"
      data-testid="community-route-community-join"
    >
      <div className="flex flex-col gap-5 text-left">
        {mode !== 'thank-you' && (
          <>
            <h1>
              <Trans
                i18nKey="join_form.title"
                values={{ name: community.name }}
                components={{
                  strong: <strong />,
                  href: <a href={`/community/${communityPath}`} className="underline" />,
                }}
              />
            </h1>
            <img
              src={community.imagePath || '/demo_community_image.png'}
              alt={community.name}
              className="rounded-lg"
            />
          </>
        )}
        {mode === 'login' && <LoginForm />}
        {mode === 'register' && !isAuthorizedUser(user) && (
          <RegisterForm
            showThankYou={() => setMode('thank-you')}
            disabled={restrictedCommunity}
            isJoinFlow
          />
        )}
        {mode === 'join' && (
          <JoinForm showRegistration={() => setMode('register')} disabled={restrictedCommunity} />
        )}
        {mode === 'thank-you' && (
          <>
            <h1>{t('registration.thank_you.title')}</h1>
            <p>{t('registration.thank_you.description')}</p>
          </>
        )}
        {mode !== 'join' && mode !== 'thank-you' && (
          <>
            <div className="relative h-5 justify-center">
              <div className="absolute top-1/2 left-1/2 z-10 -translate-1/2 bg-bg-primary p-2 text-sm">
                {t('global.delimiter.or')}
              </div>
              <hr className="absolute top-1/2 h-px w-full border-0 bg-border-100/30" />
            </div>
            <Button
              key="register"
              variant="primary"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            >
              {t(
                mode === 'login'
                  ? 'join_form.button.register.title'
                  : 'join_form.button.login.title',
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

const JoinLayout = ({
  children,
  domainAssets,
}: PropsWithChildren & { domainAssets?: DomainAssetsInterface }): ReactNode => {
  const { t } = useTranslation()

  const { brand } = useBrand()

  return (
    <>
      <Head>
        <title>{`${t('join_form.button.join.title')}!`}</title>
      </Head>
      <div className="flex min-h-screen flex-col">
        <Header
          logoSrc={domainAssets?.logoUrl || brand.logo?.path}
          headerContainerClassName="mx-auto max-w-8xl px-5 sm:px-5 md:px-5"
        />
        <div className="mx-auto grid max-w-8xl flex-1 place-items-center px-5">{children}</div>
      </div>
    </>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

Join.getLayout = (page: ReactNode, props: { domainAssets?: DomainAssetsInterface }) => (
  <JoinLayout domainAssets={props.domainAssets}>{page}</JoinLayout>
)

export default Join
